import {useEffect, useState, useCallback} from 'react';
import {
  sendCreateFavoriteGroupClickLog,
  sendGroupTabClickLog,
} from '@/utils/main/my/log';
import {throwDialog} from '@/utils/ui/dialog';

/**
 * 즐겨찾기 그룹 탭 관련 상태와 로직을 관리하는 훅
 * @returns {Object} favoriteGroupTabs, handleGroupTabChanged, handleGroupAdd, handleGroupEdit
 */
export const useFavoriteGroupTabs = (
  getFavoriteGroups,
  favoriteGroups,
  setRefreshTimestamp,
) => {
  const TOTAL_TAB = {
    name: '전체',
    query: {},
  };

  const [favoriteGroupTabs, setFavoriteGroupTabs] = useState([TOTAL_TAB]);

  useEffect(() => {
    if (favoriteGroups?.length === 0) {
      setFavoriteGroupTabs([TOTAL_TAB]);
    }

    if (favoriteGroups?.length > 0) {
      setFavoriteGroupTabs(() => {
        return [TOTAL_TAB, ...createFavoriteGroupTabs(favoriteGroups)];
      });
    }
  }, [favoriteGroups]);

  useEffect(() => {
    getFavoriteGroups();
  }, [getFavoriteGroups]);

  /**
   * 그룹 탭 변경 이벤트 핸들러
   * @param {Object} query - 그룹 정보를 포함한 쿼리 객체
   */
  const handleGroupTabChanged = useCallback(
    (query) => {
      setRefreshTimestamp();

      if (!query || query.groupId === undefined) {
        return;
      }

      const {groupId} = query;

      if (groupId === '') {
        sendGroupTabClickLog({
          groupId,
          groupName: '',
        });
        return;
      }

      const selectedGroup = favoriteGroups.find(
        (group) => Number(group.idx) === Number(groupId),
      );

      if (!selectedGroup) {
        return;
      }

      const groupName = selectedGroup.title;

      sendGroupTabClickLog({
        groupId,
        groupName,
      });
    },
    [favoriteGroups, setRefreshTimestamp],
  );

  /**
   * 그룹 추가 이벤트 핸들러
   */
  const handleGroupAdd = useCallback(() => {
    sendCreateFavoriteGroupClickLog({
      buttonType: 'create_method1',
    });

    throwDialog(
      'my/ExtendedNewGroup',
      {getFavoriteGroups},
      {isCustomDialog: true, customClassName: 'myFav_group_layer'},
    );
  }, [getFavoriteGroups]);

  /**
   * 그룹 편집 이벤트 핸들러
   */
  const handleGroupEdit = useCallback(() => {
    throwDialog(
      'my/FavoriteGroupEdit',
      {getFavoriteGroups, groups: favoriteGroups},
      {isCustomDialog: false, customClassName: 'myFav_group_layer'},
    );
  }, [getFavoriteGroups, favoriteGroups]);

  return {
    favoriteGroupTabs,
    handleGroupTabChanged,
    handleGroupAdd,
    handleGroupEdit,
  };
};

/**
 * 즐겨찾기 그룹 탭 생성
 * @param {Array} favoriteGroups - 즐겨찾기 그룹 목록
 * @returns {Array} 즐겨찾기 그룹 탭 목록
 */
function createFavoriteGroupTabs(favoriteGroups) {
  return favoriteGroups.map((group) => ({
    name: group.title,
    query: {
      groupId: String(group.idx),
    },
  }));
}
